import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Input,
  Label,
  FormGroup,
  Alert,
} from "reactstrap";
import CommonCardHeader from "../../Utils/CommonComponents/CommonCardHeader";
import {
  generateSceneDescriptions,
  generateImageForScene,
  uploadImageToS3,
  uploadToLeonardo,
  generateVideos,
  checkVideoStatus,
} from "./geminiService";
import refData from "./ref.md";

const Stories = () => {
  const [prompt, setPrompt] = useState("");
  const [numScenes, setNumScenes] = useState(3);
  const [selectedImage, setSelectedImage] = useState(null);
  const [mascotImage, setMascotImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [error, setError] = useState("");
  const [regeneratingScene, setRegeneratingScene] = useState(null);
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [videoResults, setVideoResults] = useState(null);
  const [videoStatuses, setVideoStatuses] = useState([]);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [currentStep, setCurrentStep] = useState('');
  const [generatingSceneIndex, setGeneratingSceneIndex] = useState(-1);
  const [videoGenerationStep, setVideoGenerationStep] = useState('');
  const [videoProgress, setVideoProgress] = useState({ current: 0, total: 0 });

  const hasAllImages = scenes.length > 0 && scenes.every(scene => scene.imageUrl);

  useEffect(() => {
    // Load mascot reference image
    try {
      const mascotData = refData?.contents?.[0]?.parts?.[0]?.inlineData;
      if (mascotData?.data) {
        setMascotImage({
          base64Data: mascotData.data,
          mimeType: mascotData.mimeType || "image/jpeg",
        });
      }
    } catch (err) {
      console.warn("Could not load mascot reference:", err);
    }
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result.split(",")[1];
        setSelectedImage({
          base64Data,
          mimeType: file.type,
          file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const generateStory = async () => {
    if (!prompt.trim()) {
      setError("Please enter a story prompt");
      return;
    }

    setLoading(true);
    setError("");
    setScenes([]);
    setCurrentStep('Generating scene descriptions...');

    try {
      // Generate scene descriptions
      const sceneDescriptions = await generateSceneDescriptions(
        prompt,
        numScenes
      );

      // Initialize scenes with descriptions only
      const initialScenes = sceneDescriptions.map(scene => ({
        ...scene,
        imageUrl: null,
        isGeneratingImage: false,
        error: null
      }));
      setScenes(initialScenes);
      setCurrentStep('Generating images for each scene...');

      // Generate images for each scene progressively
      const characterRef =
        selectedImage ||
        (prompt.toLowerCase().includes("mascot") ? mascotImage : null);
      
      const updatedScenes = [...initialScenes];
      
      for (let i = 0; i < sceneDescriptions.length; i++) {
        const scene = sceneDescriptions[i];
        setGeneratingSceneIndex(i);
        setCurrentStep(`Generating image for Scene ${scene.scene_number}...`);
        
        // Mark scene as generating
        updatedScenes[i] = { ...updatedScenes[i], isGeneratingImage: true };
        setScenes([...updatedScenes]);
        
        try {
          const imageUrl = await generateImageForScene(scene, characterRef);
          updatedScenes[i] = {
            ...updatedScenes[i],
            imageUrl,
            isGeneratingImage: false,
            error: null,
          };
        } catch (err) {
          console.error(
            `Scene ${scene.scene_number} image generation failed:`,
            err
          );
          updatedScenes[i] = {
            ...updatedScenes[i],
            imageUrl: null,
            isGeneratingImage: false,
            error: err.message,
          };
        }
        
        setScenes([...updatedScenes]);
      }
      
      setCurrentStep('Story generation complete!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setGeneratingSceneIndex(-1);
      setCurrentStep('');
    }
  };

  const regenerateSceneImage = async (sceneIndex) => {
    setRegeneratingScene(sceneIndex);
    setError("");

    try {
      const scene = scenes[sceneIndex];
      const characterRef =
        selectedImage ||
        (prompt.toLowerCase().includes("mascot") ? mascotImage : null);
      const imageUrl = await generateImageForScene(scene, characterRef);

      const updatedScenes = [...scenes];
      updatedScenes[sceneIndex] = {
        ...scene,
        imageUrl,
        error: null,
      };
      setScenes(updatedScenes);
    } catch (err) {
      console.error(`Scene ${sceneIndex + 1} regeneration failed:`, err);
      const updatedScenes = [...scenes];
      updatedScenes[sceneIndex] = {
        ...scenes[sceneIndex],
        error: err.message,
      };
      setScenes(updatedScenes);
    } finally {
      setRegeneratingScene(null);
    }
  };

  const handleGenerateVideos = async () => {
    setGeneratingVideos(true);
    setError("");
    setVideoProgress({ current: 0, total: scenes.length });
    
    try {
      const scenesForVideo = [];
      
      setVideoGenerationStep('Preparing scenes for video generation...');
      
      for (let i = 0; i < scenes.length; i++) {
        const scene = scenes[i];
        setVideoProgress({ current: i + 1, total: scenes.length });
        
        if (!scene.imageUrl) {
          throw new Error(`Scene ${scene.scene_number} has no image`);
        }
        
        // Upload image to S3
        setVideoGenerationStep(`Uploading Scene ${scene.scene_number} image to cloud storage...`);
        const imageName = `scene_${scene.scene_number}_${Date.now()}`;
        const s3Url = await uploadImageToS3(scene.imageUrl, imageName);
        
        // Upload to Leonardo
        setVideoGenerationStep(`Preparing Scene ${scene.scene_number} for video generation...`);
        const { leonardo_id } = await uploadToLeonardo(s3Url, imageName);
        
        scenesForVideo.push({
          scene_number: scene.scene_number,
          video_prompt: scene.video_prompt,
          leonardo_id: leonardo_id,
        });
      }
      
      // Generate videos for all scenes
      setVideoGenerationStep('Submitting all scenes for video generation...');
      const result = await generateVideos(scenesForVideo);
      setVideoResults(result);
      
      setVideoGenerationStep('Videos submitted successfully! Monitoring progress...');
      
      // Start checking status
      if (result.job_ids && result.job_ids.length > 0) {
        startStatusChecking(result.job_ids);
      }
      
    } catch (err) {
      console.error('Video generation failed:', err);
      setError(err.message);
      setVideoGenerationStep('');
    } finally {
      setGeneratingVideos(false);
    }
  };

  const startStatusChecking = (jobIds) => {
    const interval = setInterval(async () => {
      try {
        setCheckingStatus(true);
        const statusResult = await checkVideoStatus(jobIds);
        setVideoStatuses(statusResult.job_statuses || []);
        
        // Stop checking if all videos are complete
        const allComplete = statusResult.job_statuses?.every(
          status => status.status === 'COMPLETE'
        );
        
        if (allComplete) {
          clearInterval(interval);
          setCheckingStatus(false);
        }
      } catch (err) {
        console.error('Status check failed:', err);
      }
    }, 10000); // Check every 10 seconds
  };

  const resetForm = () => {
    setScenes([]);
    setVideoResults(null);
    setVideoStatuses([]);
    setShowForm(true);
    setError('');
    setVideoGenerationStep('');
    setVideoProgress({ current: 0, total: 0 });
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CommonCardHeader title="AI Story Generator" />
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {showForm && (
                <>
                <Row className="mb-4">
                  <Col md={6}>
                    <FormGroup>
                      <Label>Story Prompt</Label>
                      <Input
                        type="textarea"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Enter your story idea..."
                        rows={4}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Number of Scenes</Label>
                      <Input
                        type="select"
                        value={numScenes}
                        onChange={(e) => setNumScenes(parseInt(e.target.value))}
                      >
                        <option value={2}>2 Scenes</option>
                        <option value={3}>3 Scenes</option>
                        <option value={4}>4 Scenes</option>
                        <option value={5}>5 Scenes</option>
                        <option value={6}>6 Scenes</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>Character Image (Optional)</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      {(selectedImage || mascotImage) && (
                        <div className="mt-2">
                          <img
                            src={`data:${
                              (selectedImage || mascotImage).mimeType
                            };base64,${
                              (selectedImage || mascotImage).base64Data
                            }`}
                            alt="Character Reference"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                          <p className="small text-muted mt-1">
                            {selectedImage
                              ? "Custom character"
                              : "Mascot reference"}{" "}
                            - will appear in scenes when mentioned
                          </p>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Button
                  color="primary"
                  onClick={() => {
                    generateStory();
                    setShowForm(false);
                  }}
                  disabled={loading || !prompt.trim()}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-1"></i>
                      Generating Story...
                    </>
                  ) : (
                    "Generate Story"
                  )}
                </Button>
                </>
                )}

                {(loading || scenes.length > 0) && (
                  <div className="mt-4">
                    {loading && (
                      <Alert color="info" className="mb-3">
                        <div className="d-flex align-items-center">
                          <i className="fa fa-spinner fa-spin me-2"></i>
                          <div>
                            <strong>{currentStep}</strong>
                            {generatingSceneIndex >= 0 && (
                              <div className="mt-1">
                                <small>Progress: {generatingSceneIndex + 1} of {numScenes} scenes</small>
                              </div>
                            )}
                          </div>
                        </div>
                      </Alert>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5>Generated Story Scenes</h5>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={resetForm}
                      >
                        <i className="fa fa-plus me-1"></i>
                        New Story
                      </Button>
                    </div>
                    
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th width="10%">Scene</th>
                            <th width="25%">Image</th>
                            <th width="40%">Description</th>
                            <th width="15%">Video Prompt</th>
                            <th width="10%">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scenes.map((scene, index) => (
                            <tr key={scene.scene_number}>
                              <td className="align-middle text-center">
                                <strong>Scene {scene.scene_number}</strong>
                              </td>
                              <td className="text-center">
                                {scene.imageUrl ? (
                                  <img
                                    src={scene.imageUrl}
                                    alt={`Scene ${scene.scene_number}`}
                                    className="img-fluid rounded"
                                    style={{
                                      height: "120px",
                                      width: "120px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : scene.isGeneratingImage ? (
                                  <div
                                    className="bg-light d-flex align-items-center justify-content-center rounded"
                                    style={{ height: "120px", width: "120px", margin: "0 auto" }}
                                  >
                                    <div className="text-center">
                                      <i className="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                                      <br />
                                      <small className="text-muted">Generating...</small>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="bg-light d-flex align-items-center justify-content-center rounded"
                                    style={{ height: "120px", width: "120px", margin: "0 auto" }}
                                  >
                                    {scene.error ? (
                                      <small className="text-danger text-center">
                                        Failed
                                      </small>
                                    ) : (
                                      <small className="text-muted text-center">
                                        Waiting...
                                      </small>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="align-middle">
                                <p className="mb-0 small">{scene.description}</p>
                                {scene.isGeneratingImage && (
                                  <div className="mt-1">
                                    <span className="badge bg-primary">
                                      <i className="fa fa-spinner fa-spin me-1"></i>
                                      Generating Image
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="align-middle">
                                {scene.video_prompt && (
                                  <p className="mb-0 small text-muted">
                                    {scene.video_prompt}
                                  </p>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => regenerateSceneImage(index)}
                                  disabled={regeneratingScene === index}
                                >
                                  {regeneratingScene === index ? (
                                    <i className="fa fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fa fa-refresh"></i>
                                  )}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {hasAllImages && (
                      <div className="mt-3 text-center">
                        <Button
                          color="success"
                          size="lg"
                          onClick={handleGenerateVideos}
                          disabled={generatingVideos}
                        >
                          {generatingVideos ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              Generating Videos...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-video me-2"></i>
                              Generate Videos for All Scenes
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {generatingVideos && (
                      <div className="mt-3">
                        <Alert color="primary">
                          <div className="d-flex align-items-center">
                            <i className="fa fa-cog fa-spin me-2"></i>
                            <div className="flex-grow-1">
                              <strong>{videoGenerationStep}</strong>
                              {videoProgress.total > 0 && (
                                <div className="mt-2">
                                  <div className="progress" style={{ height: '8px' }}>
                                    <div 
                                      className="progress-bar" 
                                      style={{ 
                                        width: `${(videoProgress.current / videoProgress.total) * 100}%` 
                                      }}
                                    ></div>
                                  </div>
                                  <small className="text-muted mt-1 d-block">
                                    Processing: {videoProgress.current} of {videoProgress.total} scenes
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </Alert>
                      </div>
                    )}
                    
                    {videoResults && (
                      <div className="mt-4">
                        <Alert color="success">
                          <h6><i className="fa fa-check-circle me-2"></i>Video Generation Started!</h6>
                          <p>Total scenes: {videoResults.total_scenes}</p>
                          <p>Status: {videoResults.status}</p>
                          <p>Estimated completion: {videoResults.estimated_completion}</p>
                          
                          {videoStatuses.length > 0 && (
                            <div className="mt-3">
                              <h6>Video Status:</h6>
                              <div className="row">
                                {videoStatuses.map((status, index) => {
                                  const sceneNumber = scenes.find(s => 
                                    videoResults.leonardo_jobs?.find(job => 
                                      job.leonardo_job_id === status.jobId && job.scene_number === s.scene_number
                                    )
                                  )?.scene_number || index + 1;
                                  
                                  return (
                                    <div key={status.jobId} className="col-md-6 mb-3">
                                      <div className="card">
                                        <div className="card-body p-3">
                                          <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>Scene {sceneNumber}</strong>
                                            <span className={`badge ${
                                              status.status === 'COMPLETE' ? 'bg-success' : 
                                              status.status === 'FAILED' ? 'bg-danger' : 'bg-warning'
                                            }`}>
                                              {status.status}
                                            </span>
                                          </div>
                                          {status.status === 'COMPLETE' && status.videoUrl ? (
                                            <video 
                                              controls 
                                              width="100%" 
                                              height="150"
                                              className="rounded"
                                            >
                                              <source src={status.videoUrl} type="video/mp4" />
                                            </video>
                                          ) : (
                                            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
                                              {status.status === 'FAILED' ? (
                                                <span className="text-danger">
                                                  <i className="fa fa-exclamation-triangle fa-2x"></i>
                                                  <br />
                                                  <small>Failed</small>
                                                </span>
                                              ) : (
                                                <span className="text-muted">
                                                  <i className="fa fa-clock-o fa-2x"></i>
                                                  <br />
                                                  <small>Processing...</small>
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {checkingStatus && (
                                <div className="text-center mt-2">
                                  <i className="fa fa-spinner fa-spin me-1"></i>
                                  <small>Checking video status...</small>
                                </div>
                              )}
                            </div>
                          )}
                        </Alert>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Stories;
